import React from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Chip,
    Stack,
    Tooltip,
    Badge,
    Paper,
    LinearProgress
} from '@mui/material';
import { EmojiEvents, Star, Lock } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

const BadgesShowcase = ({ badges = [], userStats, loading = false }) => {
    // Enhanced badge definitions with database-driven approach
    const getBadgeRequirementText = (badge) => {
        // Use the actual badge data from backend with new semantic fields
        if (badge.category === "STREAK" && badge.requiredStreakDays > 0) {
            return `${badge.requiredStreakDays}-day streak`;
        }
        if (badge.category === "ACCURACY" && badge.requiredAccuracy > 0) {
            return `${badge.requiredClassifications} consecutive ${badge.requiredAccuracy}%+ confident`;
        }
        if (badge.category === "SPECIAL" && badge.requiredFeedbacks > 0) {
            return `${badge.requiredFeedbacks} accepted corrections`;
        }
        if (badge.requiredClassifications > 0) {
            return `${badge.requiredClassifications} classifications`;
        }
        return badge.description;
    };

    const getBadgeProgress = (badge) => {
        if (!userStats) return 0;

        // Calculate progress based on badge category and requirements
        switch (badge.category) {
            case "ACHIEVEMENT":
            case "MILESTONE":
                if (badge.requiredClassifications > 0) {
                    return Math.min(100, (userStats.totalClassifications / badge.requiredClassifications) * 100);
                }
                break;
            case "STREAK":
                if (badge.requiredStreakDays > 0) {
                    return Math.min(100, (userStats.currentStreak / badge.requiredStreakDays) * 100);
                }
                break;
            case "ACCURACY":
                // For Sharpshooter: need consecutive high-confidence classifications
                // This would need backend support to track consecutive streaks
                return badge.isUnlocked ? 100 : 0;
            case "SPECIAL":
                // For Correctionist: need accepted feedback corrections
                // This would need backend support to track feedback status
                return badge.isUnlocked ? 100 : 0;
            default:
                return badge.isUnlocked ? 100 : 0;
        }
        return 0;
    };

    // Get all badges (both earned and available) from database
    const allBadges = [
        {
            name: "Green Starter",
            description: "First time waste classifier",
            category: "ACHIEVEMENT",
            rarity: "COMMON",
            requiredClassifications: 1,
            requiredStreakDays: 0,
            requiredAccuracy: 0,
            requiredFeedbacks: 0,
            iconUrl: "🌱",
            isUnlocked: badges.some(b => b.name === "Green Starter") || userStats?.totalClassifications >= 1
        },
        {
            name: "Marathoner",
            description: "100 total classifications!",
            category: "MILESTONE",
            rarity: "RARE",
            requiredClassifications: 100,
            requiredStreakDays: 0,
            requiredAccuracy: 0,
            requiredFeedbacks: 0,
            iconUrl: "🏃‍♂️",
            isUnlocked: badges.some(b => b.name === "Marathoner") || userStats?.totalClassifications >= 100
        },
        {
            name: "Weekly Warrior",
            description: "7-day classification streak!",
            category: "STREAK",
            rarity: "EPIC",
            requiredClassifications: 0,
            requiredStreakDays: 7,
            requiredAccuracy: 0,
            requiredFeedbacks: 0,
            iconUrl: "⚔️",
            isUnlocked: badges.some(b => b.name === "Weekly Warrior") || userStats?.currentStreak >= 7
        },
        {
            name: "Sharpshooter",
            description: "10 high-confidence in a row!",
            category: "ACCURACY",
            rarity: "EPIC",
            requiredClassifications: 10,
            requiredStreakDays: 0,
            requiredAccuracy: 90,
            requiredFeedbacks: 0,
            iconUrl: "🎯",
            isUnlocked: badges.some(b => b.name === "Sharpshooter")
        },
        {
            name: "Correctionist",
            description: "5 accepted corrections!",
            category: "SPECIAL",
            rarity: "LEGENDARY",
            requiredClassifications: 0,
            requiredStreakDays: 0,
            requiredAccuracy: 0,
            requiredFeedbacks: 5,
            iconUrl: "✨",
            isUnlocked: badges.some(b => b.name === "Correctionist")
        }
    ];

    const getRarityColor = (rarity) => {
        const colors = {
            COMMON: { bg: '#4caf50', border: '#43a047', shadow: 'rgba(76, 175, 80, 0.3)' },
            RARE: { bg: '#2196f3', border: '#1976d2', shadow: 'rgba(33, 150, 243, 0.3)' },
            EPIC: { bg: '#ff9800', border: '#f57c00', shadow: 'rgba(255, 152, 0, 0.3)' },
            LEGENDARY: { bg: '#e91e63', border: '#c2185b', shadow: 'rgba(233, 30, 99, 0.3)' }
        };
        return colors[rarity] || colors.COMMON;
    };

    const isEarned = (badgeName) => {
        return badges.some(badge => badge.name === badgeName);
    };

    const getEarnedBadge = (badgeName) => {
        return badges.find(badge => badge.name === badgeName);
    };

    if (loading) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h5">Loading your eco achievements...</Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h4" fontWeight={800} sx={{ mb: 1, color: '#1b5e20', textAlign: 'center' }}>
                🏅 Your Eco Badge Collection
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
                {badges.length} of {allBadges.length} badges unlocked • Keep sorting to unlock more!
            </Typography>

            {/* Earned Badges Section */}
            {badges.length > 0 && (
                <Paper elevation={4} sx={{ mb: 4, borderRadius: 3, p: 3, background: 'linear-gradient(135deg, #f8fff8, #e8f5e8)' }}>
                    <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: '#2e7d32', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmojiEvents sx={{ color: '#ffc400' }} />
                        Earned Badges ({badges.length})
                    </Typography>
                    <Grid container spacing={3}>
                        {badges.map((badge, index) => {
                            const badgeStyle = getRarityColor(badge.rarity);
                            return (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Card
                                        elevation={8}
                                        sx={{
                                            background: badgeStyle.bg,
                                            color: 'white',
                                            borderRadius: 4,
                                            border: `3px solid ${badgeStyle.border}`,
                                            boxShadow: `0 12px 24px ${badgeStyle.shadow}`,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-8px) scale(1.05)',
                                                boxShadow: `0 20px 40px ${badgeStyle.shadow}`
                                            }
                                        }}
                                    >
                                        <CardContent sx={{ textAlign: 'center', p: 3 }}>
                                            <Typography variant="h2" sx={{ mb: 2 }}>
                                                {badge.iconUrl || '🏆'}
                                            </Typography>
                                            <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
                                                {badge.name}
                                            </Typography>
                                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                                                {badge.description}
                                            </Typography>
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                <Chip
                                                    label={badge.rarity}
                                                    size="small"
                                                    sx={{
                                                        background: 'rgba(255,255,255,0.2)',
                                                        color: 'white',
                                                        fontWeight: 700
                                                    }}
                                                />
                                                <Chip
                                                    label={badge.category}
                                                    size="small"
                                                    sx={{
                                                        background: 'rgba(255,255,255,0.2)',
                                                        color: 'white',
                                                        fontWeight: 700
                                                    }}
                                                />
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Paper>
            )}

            {/* All Badges Progress */}
            <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: '#1b5e20' }}>
                🎯 Badge Progress & Requirements
            </Typography>
            <Grid container spacing={3}>
                {allBadges.map((badge, index) => {
                    const earned = isEarned(badge.name);
                    const earnedBadge = earned ? getEarnedBadge(badge.name) : null;
                    const badgeStyle = getRarityColor(badge.rarity);

                    return (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Tooltip
                                title={
                                    <Box sx={{ p: 1 }}>
                                        <Typography variant="subtitle2" fontWeight={700}>
                                            {badge.name}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                            {badge.description}
                                        </Typography>
                                        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                                            {earned ? 'Unlocked!' : `Requirements: ${getBadgeRequirementText(badge)}`}
                                        </Typography>
                                    </Box>
                                }
                                arrow
                            >
                                <Card
                                    elevation={earned ? 8 : 2}
                                    sx={{
                                        background: earned
                                            ? badgeStyle.bg
                                            : 'linear-gradient(135deg, #f5f5f5, #eeeeee)',
                                        color: earned ? 'white' : '#666666',
                                        borderRadius: 4,
                                        border: earned
                                            ? `3px solid ${badgeStyle.border}`
                                            : '3px solid #cccccc',
                                        opacity: earned ? 1 : 0.6,
                                        boxShadow: earned
                                            ? `0 8px 24px ${badgeStyle.shadow}`
                                            : '0 4px 12px rgba(0,0,0,0.1)',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            transform: earned ? 'translateY(-5px) scale(1.02)' : 'translateY(-2px)',
                                            opacity: earned ? 1 : 0.8
                                        }
                                    }}
                                >
                                    <CardContent sx={{ textAlign: 'center', p: 2.5, position: 'relative' }}>
                                        {!earned && (
                                            <Lock sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                fontSize: 20,
                                                color: '#999999'
                                            }} />
                                        )}

                                        <Typography variant="h3" sx={{
                                            mb: 1,
                                            filter: earned ? 'none' : 'grayscale(100%)'
                                        }}>
                                            {badge.iconUrl || '🏆'}
                                        </Typography>

                                        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                                            {badge.name}
                                        </Typography>

                                        <Typography variant="body2" sx={{
                                            opacity: earned ? 0.9 : 0.7,
                                            mb: 2,
                                            fontSize: '0.85rem'
                                        }}>
                                            {badge.description}
                                        </Typography>

                                        <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
                                            <Chip
                                                label={badge.rarity}
                                                size="small"
                                                sx={{
                                                    background: earned ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                                                    color: earned ? 'white' : '#666666',
                                                    fontWeight: 600,
                                                    fontSize: '0.7rem',
                                                    height: 20
                                                }}
                                            />
                                            <Chip
                                                label={badge.category}
                                                size="small"
                                                sx={{
                                                    background: earned ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                                                    color: earned ? 'white' : '#666666',
                                                    fontWeight: 600,
                                                    fontSize: '0.7rem',
                                                    height: 20
                                                }}
                                            />
                                        </Stack>

                                        {earned && earnedBadge && (
                                            <Badge
                                                badgeContent={<Star sx={{ fontSize: 12 }} />}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 8,
                                                    right: 8,
                                                    '& .MuiBadge-badge': {
                                                        background: '#ffc400',
                                                        color: 'white'
                                                    }
                                                }}
                                            />
                                        )}

                                        {/* Progress Bar for Requirements */}
                                        {!earned && (
                                            <Box sx={{ mt: 2 }}>
                                                <LinearProgress variant="determinate" value={getBadgeProgress(badge)} sx={{ height: 8, borderRadius: 4 }}>
                                                    <Box sx={{ position: 'absolute', width: '100%', display: 'flex', justifyContent: 'center', top: 0 }}>
                                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                            {`${Math.round(getBadgeProgress(badge))}%`}
                                                        </Typography>
                                                    </Box>
                                                </LinearProgress>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Tooltip>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Badge Statistics */}
            <Paper elevation={4} sx={{ mt: 4, p: 3, borderRadius: 3, background: 'linear-gradient(135deg, #fff8e1, #f3e5f5)' }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: '#1b5e20' }}>
                    📈 Badge Collection Statistics
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" fontWeight={800} sx={{ color: '#4caf50' }}>
                                {badges.filter(b => b.rarity === 'COMMON').length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Common</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" fontWeight={800} sx={{ color: '#2196f3' }}>
                                {badges.filter(b => b.rarity === 'RARE').length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Rare</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" fontWeight={800} sx={{ color: '#ff9800' }}>
                                {badges.filter(b => b.rarity === 'EPIC').length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Epic</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" fontWeight={800} sx={{ color: '#e91e63' }}>
                                {badges.filter(b => b.rarity === 'LEGENDARY').length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Legendary</Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
};

export default BadgesShowcase;
